/**
 * Which application this page is: the workspace shell, or the platform console.
 *
 * One image serves both (the owner's decision, 2026-09-26): the console opens at its own address,
 * `admin-core.dash.ps`, and nowhere inside a workspace links to it. The address decides, before any
 * other code loads, so the workspace shell's code, routes and tokens are never fetched in the
 * console and the console's never in a workspace.
 *
 * In development there is one address for both, so two flags open the console there and only there:
 * `#platform` (the console's own pages then live under it, `#platform/reports`) and `?console`.
 * A production build ignores both: an address a person can type is not how the console is reached.
 *
 * Pure: no `window`, so a test says what each address opens.
 */
export interface Place { hostname: string; hash: string; search: string }

export type Mode =
  | { app: 'workspace' }
  /** `prefix` is what every console link starts with: `#` on its own address, `#platform/` behind the hash flag. */
  | { app: 'console'; via: 'host' | 'hash' | 'query'; prefix: string };

/**
 * The console's own address starts with one of these labels: `admin-core.dash.ps` (and
 * `admin-core.localhost` on a machine), or `admin.` on an installation whose hosts carry no `-core`
 * label (`admin.erp.sslab.om`, beside `app.` and `api.`).
 */
export const CONSOLE_HOST_LABELS = ['admin-core.', 'admin.'] as const;

export function modeOf(place: Place, dev: boolean): Mode {
  const host = place.hostname.toLowerCase();
  if (CONSOLE_HOST_LABELS.some((label) => host.startsWith(label))) return { app: 'console', via: 'host', prefix: '#' };
  if (dev) {
    const hash = place.hash.replace(/^#\/?/, '');
    if (hash === 'platform' || hash.startsWith('platform/')) return { app: 'console', via: 'hash', prefix: '#platform/' };
    if (new URLSearchParams(place.search).has('console')) return { app: 'console', via: 'query', prefix: '#' };
  }
  return { app: 'workspace' };
}
